import { useCallback, useMemo } from 'react';
import { ILead } from 'src/interfaces/lead';
import { STATUSES } from 'src/constants/mongo/status';
import { WHATSAPP_CHANNEL } from 'src/constants/conversations';
import { ASSISTANT_INTENT, CHAT_SCOPE } from 'src/contexts/assistant/constants';
import { TResolvedChannel } from 'src/modules/conversations/channels';
import { getChannelName } from 'src/utils/format/getChannelName';
import { getRecommendationActions } from 'src/modules/assistant/helpers/recommendationActions';
import { resolveLeadChannel } from 'src/modules/assistant/helpers/resolveLeadChannel';
import { useLeadChannelConversation } from './useLeadChannelConversation';
import { TSelectedLead, TChatScope } from 'src/contexts/assistant/types';
import {
    TRecommendation,
    TRecommendationOption,
} from 'src/modules/assistant/types/message';

const RECOMMENDED_TEMPLATE_LABEL = 'Assistant.ActionRecommendedTemplate';
const REPLY_BY_CHANNEL_LABEL = 'Assistant.ActionReplyByChannel';
const REPLY_INTENTS: string[] = [
    ASSISTANT_INTENT.CONVERSATION,
    ASSISTANT_INTENT.VIEW_APPOINTMENT,
];

interface UseAvailableActionsParams {
    lead: ILead | null;
    selectedLead: TSelectedLead | null;
    scope: TChatScope;
}

const isOptionAvailable = (
    intent: string,
    recommendation: TRecommendation,
    lead: ILead,
    channel: TResolvedChannel
): boolean => {
    if (intent === ASSISTANT_INTENT.ASSIGN) return false;
    if (intent === ASSISTANT_INTENT.APPOINTMENT) return !lead.visits?.length;
    if (intent === ASSISTANT_INTENT.TASK)
        return lead.status?._id !== STATUSES.REJECTED.ID;
    if (intent === ASSISTANT_INTENT.WHATSAPP)
        return (
            channel.channel === WHATSAPP_CHANNEL &&
            (recommendation.templateId
                ? channel.canSendTemplate
                : channel.canSendFreeForm)
        );
    if (REPLY_INTENTS.includes(intent)) return channel.canSendFreeForm;
    return true;
};

const labelOption = (
    option: TRecommendationOption,
    recommendation: TRecommendation,
    channel: TResolvedChannel
): TRecommendationOption => {
    if (
        option.intent === ASSISTANT_INTENT.WHATSAPP &&
        recommendation.templateId
    )
        return { ...option, label: RECOMMENDED_TEMPLATE_LABEL };
    if (option.intent === ASSISTANT_INTENT.CONVERSATION)
        return {
            ...option,
            label: REPLY_BY_CHANNEL_LABEL,
            labelParams: { channel: getChannelName(channel.channel) },
        };
    return option;
};

/**
 * Actions the assistant can offer for a recommendation, given the lead loaded in
 * LeadContext and what its conversation channel allows. Nothing is offered when
 * the chat lead is not loaded, has no agent or its channel conversation is loading.
 */
export const useAvailableActions = ({
    lead,
    selectedLead,
    scope,
}: UseAvailableActionsParams) => {
    const loadedLead = lead?._id && lead._id === selectedLead?.id ? lead : null;
    const leadRequiresAgent =
        scope === CHAT_SCOPE.LEAD && Boolean(loadedLead) && !loadedLead.agent;
    const hasConversation = Boolean(selectedLead?.conversationId);
    const { conversation: channelConversation, loading: channelLoading } =
        useLeadChannelConversation(loadedLead);
    const channel = useMemo(
        () =>
            loadedLead && !channelLoading
                ? resolveLeadChannel(loadedLead, channelConversation)
                : null,
        [channelConversation, channelLoading, loadedLead]
    );

    const getAvailableActions = useCallback(
        (recommendation: TRecommendation): TRecommendationOption[] => {
            if (!loadedLead?.agent || !channel) return [];
            return (
                getRecommendationActions(
                    recommendation.action,
                    hasConversation,
                    recommendation.executionTarget
                ) as TRecommendationOption[]
            )
                .filter(({ intent }) =>
                    isOptionAvailable(
                        intent,
                        recommendation,
                        loadedLead,
                        channel
                    )
                )
                .map((option) => labelOption(option, recommendation, channel));
        },
        [channel, hasConversation, loadedLead]
    );

    return {
        loadedLead,
        leadRequiresAgent,
        channel,
        channelConversation,
        getAvailableActions,
    };
};
